import {
  STATIC_AGENT_SECTION_LABELS,
  type StaticAgentSectionKey,
} from "@repo/app-types/entities/configurationAgent";
import { IconArrowRight, IconChevronDown } from "@tabler/icons-react";
import { useState, type FC } from "react";
import { twMerge } from "tailwind-merge";
import { useSectionSuggestions } from "./sectionTesting.js";

/** Display order for the suggestion list (matches the Instructions tab). */
const SECTION_ORDER: readonly StaticAgentSectionKey[] = [
  "agent_name",
  "agent_role_and_personality",
  "caller_context",
  "conversation_flow",
  "behavior_and_guardrails",
];

/**
 * Reads the section-suggestion store and renders the navigable list. Kept
 * separate from the shared sidebar so the legacy (non-static) config surface,
 * which has no `SectionTestingProvider`, never calls the hook.
 */
export const SectionSuggestionListContainer: FC = () => {
  const { suggestions, viewedSections, requestSelectSection } =
    useSectionSuggestions();

  const pendingSections = SECTION_ORDER.filter(
    (section) => suggestions[section] !== undefined,
  );

  if (pendingSections.length === 0) {
    return null;
  }

  // Own wrapper so the list reads as a detached card above the composer box,
  // matching the playbook editor (`playbookConfigurationAssistant.tsx`).
  return (
    <div className="min-w-0 overflow-hidden px-3 pt-3 pb-2">
      <SectionSuggestionList
        sections={pendingSections}
        viewedSections={viewedSections}
        onSelect={requestSelectSection}
      />
    </div>
  );
};

export interface SectionSuggestionListProps {
  /** Sections with a pending proposal, in display order. */
  sections: readonly StaticAgentSectionKey[];
  onSelect: (section: StaticAgentSectionKey) => void;
  /** Sections currently visible in the editor, to flag the "Viewing" row. */
  viewedSections?: readonly StaticAgentSectionKey[];
  className?: string;
}

/**
 * A navigable list of the AI Assistant's pending identity-section proposals,
 * rendered above the composer. Mirrors the playbook editor's
 * `PlaybookDraftSuggestionList`, but keyed by section instead of playbook node.
 */
export const SectionSuggestionList: FC<SectionSuggestionListProps> = ({
  sections,
  onSelect,
  viewedSections,
  className,
}) => {
  const [isCollapsed, setIsCollapsed] = useState(false);

  if (sections.length === 0) {
    return null;
  }

  return (
    <div
      className={twMerge(
        "w-full min-w-0 overflow-hidden rounded-lg border border-border-light bg-white shadow-xs",
        className,
      )}
    >
      <div className="flex min-w-0 items-center justify-between gap-2 px-3 py-1.5">
        <span className="min-w-0 flex-1 truncate text-xs font-medium text-muted-foreground">
          Suggested edits
        </span>
        <button
          type="button"
          onClick={() => {
            setIsCollapsed((prev) => !prev);
          }}
          aria-expanded={!isCollapsed}
          aria-label={
            isCollapsed ? "Expand suggestions" : "Collapse suggestions"
          }
          className="-mr-1 flex size-5 shrink-0 items-center justify-center rounded-sm text-gray-400 hover:bg-gray-100 hover:text-gray-600 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
        >
          <IconChevronDown
            className={twMerge(
              "size-4 transition-transform duration-200 ease-out",
              isCollapsed && "-rotate-90",
            )}
          />
        </button>
      </div>
      <div
        className={twMerge(
          "grid transition-[grid-template-rows,opacity] duration-200 ease-out",
          isCollapsed
            ? "grid-rows-[0fr] opacity-0"
            : "grid-rows-[1fr] opacity-100",
        )}
      >
        <ul className="flex min-w-0 flex-col overflow-hidden border-t border-gray-100">
          {sections.map((section, index) => (
            <li key={section} className="min-w-0">
              <SectionSuggestionRow
                section={section}
                isFirst={index === 0}
                isViewing={Boolean(viewedSections?.includes(section))}
                onSelect={onSelect}
              />
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
};

interface SectionSuggestionRowProps {
  section: StaticAgentSectionKey;
  isFirst: boolean;
  isViewing: boolean;
  onSelect: (section: StaticAgentSectionKey) => void;
}

const SectionSuggestionRow: FC<SectionSuggestionRowProps> = ({
  section,
  isFirst,
  isViewing,
  onSelect,
}) => {
  const label = STATIC_AGENT_SECTION_LABELS[section];
  return (
    <button
      type="button"
      disabled={isViewing}
      onClick={() => {
        onSelect(section);
      }}
      className={twMerge(
        "group flex w-full items-center gap-2 px-3 py-2.5 text-left text-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-indigo-600",
        isViewing
          ? "cursor-default text-gray-400"
          : "text-foreground hover:bg-gray-50",
        !isFirst && "border-t border-gray-100",
      )}
    >
      <span className="min-w-0 flex-1 truncate" title={label}>
        {label}
      </span>
      {isViewing ? (
        <span className="shrink-0 text-xs text-gray-400">Viewing</span>
      ) : (
        <IconArrowRight className="size-4 shrink-0 text-gray-400 group-hover:text-gray-600" />
      )}
    </button>
  );
};
